// # 길게 누르기 — 손가락에는 오른쪽 클릭도, 지나가기(hover)도 없다
//
// 마우스로는 줄에 지나가면 손이 뜨고 오른쪽 클릭이 메뉴를 연다. 손가락으로는 둘 다 없어, 전화에서는
// 메시지의 더 보기·채널 줄의 옵션에 닿을 길이 없었다. 슬랙 모바일처럼 **길게 누르면** 같은 메뉴가 뜬다.
//
// - 터치(`pointerType === 'touch'`)에만 건다. 마우스·펜은 지나가기와 오른쪽 클릭이 있다.
// - 0.5초(`LONG_PRESS_MS`)를 채우면 `fire`. 그 전에 손가락이 10px(`LONG_PRESS_SLOP`) 넘게 움직이면 스크롤이지
//   누름이 아니다 — 취소한다. 브라우저가 스크롤을 잡으면 `pointercancel` 이 오고, 그것도 취소다.
// - 채우지 못하고 뗐으면 짧게 누른 것(`tap`). 열린 것을 닫는 데만 쓴다 — 탭으로 무언가를 **띄우면**
//   대화를 짚을 때마다 그것이 떠서 사람을 괴롭힌다(메시지 줄의 이모지 막대가 그랬다).
// - 길게 누른 뒤에 오는 `click` 은 삼킨다 — 안 삼키면 메뉴가 뜨면서 줄도 눌린다(채널이 바뀐다).
//   같은 까닭으로 안드로이드의 `contextmenu` 도 막는다(우리 메뉴가 그 자리다).
// - `skip` 에 맞는 자식(단추·링크·입력칸)에서 시작한 누름은 건드리지 않는다 — 그것들은 제 길게 누르기
//   (풍선·링크 콜아웃)가 있다.
//
// 판정(`press_step`)은 순수 함수다 — 브라우저 없이 시험한다(`long_press.test.ts`).

import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react'
import { useEffect, useRef } from 'react'

/** 이만큼 누르고 있으면 길게 누른 것이다. iOS·안드로이드의 기본과 같다. */
export const LONG_PRESS_MS = 500
/** 이만큼 넘게 움직이면 스크롤이다. */
export const LONG_PRESS_SLOP = 10

/** 누르고 있는 동안의 상태. `fired` 는 이미 길게 누른 것으로 쳤다는 표시다. */
export interface Press {
  x: number
  y: number
  fired: boolean
}

export type PressEvent =
  | { type: 'down'; x: number; y: number }
  | { type: 'move'; x: number; y: number }
  | { type: 'up' }
  | { type: 'cancel' }
  /** 시계가 다 됐다. */
  | { type: 'time' }

/** 사건 하나가 누름을 어떻게 바꾸고 무엇을 시키는가. `null` 은 아무것도 안 누른 상태다. */
export function press_step(
  press: Press | null,
  ev: PressEvent,
  slop = LONG_PRESS_SLOP,
): { press: Press | null; act: 'fire' | 'tap' | null } {
  switch (ev.type) {
    case 'down':
      return { press: { x: ev.x, y: ev.y, fired: false }, act: null }
    case 'move':
      if (press === null || press.fired) return { press, act: null }
      return Math.hypot(ev.x - press.x, ev.y - press.y) > slop
        ? { press: null, act: null }
        : { press, act: null }
    case 'time':
      if (press === null || press.fired) return { press, act: null }
      return { press: { ...press, fired: true }, act: 'fire' }
    case 'up':
      if (press === null) return { press: null, act: null }
      // 길게 누른 뒤의 뗌은 아무것도 아니다 — 뒤따르는 click 만 삼키면 된다(`fired` 를 남긴다).
      return press.fired ? { press, act: null } : { press: null, act: 'tap' }
    case 'cancel':
      return { press: null, act: null }
  }
}

export interface LongPressOptions {
  /** 채우지 못하고 뗐을 때. 없으면 짧은 누름은 브라우저 것이다(click 이 그대로 간다). */
  tap?: ((el: HTMLElement) => void) | undefined
  /** 이 선택자에 맞는 자식에서 시작한 누름은 건드리지 않는다. */
  skip?: string | undefined
  /** 기본 `LONG_PRESS_MS`. */
  ms?: number | undefined
  /** 길게 누른 뒤 손가락을 **뗐을 때**. 뗌 뒤에 열어야 하는 것(팝오버 메뉴)은 여기서 — 아래 `onPointerUp` 참고. */
  release?: ((el: HTMLElement) => void) | undefined
}

export interface LongPressHandlers<T extends HTMLElement> {
  onPointerDown(e: ReactPointerEvent<T>): void
  onPointerMove(e: ReactPointerEvent<T>): void
  onPointerUp(e: ReactPointerEvent<T>): void
  onPointerCancel(e: ReactPointerEvent<T>): void
  onContextMenu(e: ReactMouseEvent<T>): void
  onClickCapture(e: ReactMouseEvent<T>): void
}

/**
 * 길게 누르기 손잡이 한 벌. 돌려준 것을 요소에 그대로 편다. `fire` 는 누른 요소(`currentTarget`)를 받는다.
 * 요소에 제 `onContextMenu` 가 따로 있으면 이것을 먼저 부르고 `defaultPrevented` 를 본다.
 */
export function useLongPress<T extends HTMLElement>(
  fire: (el: T) => void,
  opts?: LongPressOptions,
): LongPressHandlers<T> {
  const press = useRef<Press | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(timer.current), [])

  // 누름이 끝나는 길(뗌·취소·너무 멀리 움직임)은 전부 시계를 멈춘다 — 판정은 `press_step` 이 한다.
  // `currentTarget` 은 사건이 끝나면 비므로(React) 시계가 울릴 때 쓸 요소는 미리 받아 둔다.
  const step = (ev: PressEvent, el: T) => {
    const next = press_step(press.current, ev, LONG_PRESS_SLOP)
    press.current = next.press
    if (next.press === null || next.act === 'fire') clearTimeout(timer.current)
    if (next.act === 'fire') fire(el)
    else if (next.act === 'tap') opts?.tap?.(el)
  }
  const touch = (e: ReactPointerEvent<T>, ev: PressEvent) => {
    if (press.current !== null && e.pointerType === 'touch') step(ev, e.currentTarget)
  }

  return {
    onPointerDown: (e) => {
      const skip = opts?.skip
      if (e.pointerType !== 'touch') return
      if (skip !== undefined && e.target instanceof Element && e.target.closest(skip) !== null) {
        press.current = null
        return
      }
      clearTimeout(timer.current)
      const el = e.currentTarget
      step({ type: 'down', x: e.clientX, y: e.clientY }, el)
      timer.current = setTimeout(() => step({ type: 'time' }, el), opts?.ms ?? LONG_PRESS_MS)
    },
    onPointerMove: (e) => touch(e, { type: 'move', x: e.clientX, y: e.clientY }),
    onPointerUp: (e) => {
      touch(e, { type: 'up' })
      if (press.current?.fired !== true) return
      // 길게 누른 뒤의 뗌에 딸린 click 은 뗌과 **같은 입력 일감** 안에서 바로 온다(안드로이드는 contextmenu 를 낸
      // 뒤라 아예 안 온다). 그러니 삼킬 표시(`fired`)는 다음 일감까지만 둔다 — 영영 두면 그 다음에 우리가 스스로
      // 누르는 click(더 보기 메뉴)까지 `onClickCapture` 가 삼켰다(에뮬레이터 실측: 막대만 서고 메뉴가 없었다).
      // `release` 는 그 뒤에 부른다. 시계가 울릴 때(손가락이 눌린 채) 팝오버를 열면 뒤따르는 뗌이 팝오버 밖의
      // pointerup 이라 가벼운 닫힘으로 바로 닫힌다.
      const el = e.currentTarget
      setTimeout(() => {
        if (press.current?.fired === true) press.current = null
        opts?.release?.(el)
      }, 0)
    },
    onPointerCancel: (e) => touch(e, { type: 'cancel' }),
    onContextMenu: (e) => {
      // 안드로이드는 길게 누르면 contextmenu 를 낸다 — 우리 메뉴가 뜨는 동안 브라우저 것이 겹치면 안 된다.
      if (press.current === null) return
      e.preventDefault()
      // 크롬(안드로이드)은 contextmenu 와 함께 글자 선택도 세운다 — 막는다고 안 서는 게 아니라 거둬야 한다.
      window.getSelection()?.removeAllRanges()
    },
    onClickCapture: (e) => {
      if (press.current?.fired !== true) return
      press.current = null
      e.preventDefault()
      e.stopPropagation()
    },
  }
}
